import { StoryVisualDescriptionTerseResponse } from "../chatgpt/chatGptTemplates";
import { DynamoKey } from "./BaseDynamoClass";
import { StripUnderscore, UnderscoreKeys } from "../util/Types";
import { BasicError } from "./BasicError";
import { StoryStatus } from "./Enums";
import { User, UserPKInput } from "./User";
import { UserOwnedClass, userOwnedSchema } from "./UserOwnedClass";
import z from "zod";


export type TitleOption = {
	index: number;
	title: string;
	synopsis: string;
};

export type StoryPageEntry = {
	page: number;
	text: string;
};

export type PageSwatch = {
	rgb: [number, number, number];
	population: number;
};

export type PagePalette = {
	LightVibrant: PageSwatch;
	DarkVibrant: PageSwatch;
	LightMuted: PageSwatch;
	DarkMuted: PageSwatch;
	Vibrant: PageSwatch;
	Muted: PageSwatch;
};

export type PageImage = {
	fileName: string;
	extension: "png" | "jpg";
	keySmall: string;
	keyStandard: string;
	keyLarge?: string;
	palette?: PagePalette;
};

export type StoryPage = object;

export type StoryInnerPage = StoryPage & {
	textIndex: number;
	textOptions: string[];
};

export type StoryImagePage = StoryPage & {
	visualDescription: StoryVisualDescriptionTerseResponse | undefined;
	imageIndex?: number;
	imageOptions?: PageImage[];
};

export type StoryCoverPage = StoryPage & StoryImagePage;

export const storyBaseSchema = userOwnedSchema.extend({
  _storyId: z.string().nonempty(),
  _titleOptions : z.any().optional(),
  _selectedTitle: z.number().optional(),
  _frontCover: z.any().optional(),
  _backCover : z.any().optional()
});

export type StoryBaseFields = StripUnderscore<z.infer<typeof storyBaseSchema>>;


export class StoryBase<TFields extends StoryBaseFields = StoryBaseFields> extends UserOwnedClass<TFields> {
	_storyId!: string;
	_titleOptions: TitleOption[] | undefined;
	_selectedTitle: number | undefined;
	_frontCover: StoryCoverPage | undefined;
	_backCover: StoryCoverPage | undefined;
	override _className: string = "Story";
	override _deserializeClassName : string  = "StoryBase";

	override getFieldList(): (keyof UnderscoreKeys<TFields>)[] {
		return [...super.getFieldList(), "_storyId", "_titleOptions", "_selectedTitle", "_frontCover", "_backCover"];
	}
	constructor(props?: Partial<TFields>) {
		super(props);
	}
	override validateSchema(): void {
		storyBaseSchema.parse(this);
	}

	get storyId(): string {
		return this.checkFieldType<"storyId">(this._storyId);
	}
	set storyId(value: string) {
		this._storyId = this.checkFieldTypeAndMark<"storyId">("_storyId", value);
	}

	get titleOptions(): TitleOption[] | undefined {
		return this.checkFieldType<"titleOptions">(this._titleOptions);
	}
	set titleOptions(value: TitleOption[] | undefined) {
		if (this._titleOptions === value) return;
		this._titleOptions = this.checkFieldTypeAndMark<"titleOptions">("_titleOptions", value);
		value === undefined ? this.setStoryState(StoryStatus.TOPIC_ANALYSED) : this.setStoryState(StoryStatus.TITLE_OPTIONS_SET);
	}

	get selectedTitle(): number | undefined {
		return this.checkFieldType<"selectedTitle">(this._selectedTitle);
	}
	set selectedTitle(value: number | undefined) {
		if (this._selectedTitle === value) return;
		this._selectedTitle = this.checkFieldTypeAndMark<"selectedTitle">("_selectedTitle", value);
		value === undefined ? this.setStoryState(StoryStatus.TITLE_OPTIONS_SET) : this.setStoryState(StoryStatus.TITLE_CHOSEN);
	}
	get frontCover(): StoryCoverPage | undefined {
		return this.checkFieldType<"frontCover">(this._frontCover);
	}
	set frontCover(value: StoryCoverPage | undefined) {
		this._frontCover = this.checkFieldTypeAndMark<"frontCover">("_frontCover", value);
	}

	get backCover(): StoryCoverPage | undefined {
		return this.checkFieldType<"backCover">(this._backCover);
	}
	set backCover(value: StoryCoverPage | undefined) {
		this._backCover = this.checkFieldTypeAndMark<"backCover">("_backCover", value);
	}

    protected setStoryState(status: StoryStatus): void {
        throw new BasicError("Can't set story status", "Using base object without full data, cannot set status");
    }
	getIgnoreFields(): string[] {
		return [...super.getIgnoreFields(), "tempStorage", "tempStorageToolCallId", "tempStorageFunctionName"];
	}
	getPKPrefix(): string {
		return "USER#";
	}
	getSKPrefix(props: object | undefined): string {
		return "STORY#";
	}
	getPK(): string {
		if (!this.userId) throw new BasicError("Missing userId", "Missing userId");
		return this.getPKPrefix() + this.userId;
	}
	getSK(): string {
		if (!this._storyId) throw new BasicError("Missing storyId", "Missing storyId");
		return this.getSKPrefix(undefined) + this._storyId;
	}
	getParentKey(): DynamoKey | undefined {
		if (!this.userId) throw new BasicError("Missing userId", "Missing userId");
		return User.generateKey({ userId: this.userId });
	}
	getParentKeyInput(): UserPKInput | undefined {
		if (!this.userId) throw new BasicError("Missing userId", "Missing userId");
		return { userId: this.userId };
	}
	getParentTableName(): string | undefined {
		return undefined;
	}
	generateKey(input: any): DynamoKey {
		if (!input.userId) throw new BasicError("Missing userId", "Missing userId");
		if (!input.storyId) throw new BasicError("Missing storyId", "Missing storyId");
		return {
			PK: this.getPKPrefix + input.userId,
			SK: this.getSKPrefix(input) + input.storyId,
		};
	}
	generatePK(input: UserPKInput): string {
		if (!input.userId) throw new BasicError("Missing userId", "Missing userId");
		return this.getPKPrefix() + input.userId;
	}
	generateGSIPK(index: number, key: object): string {
		throw new BasicError("Not implemented", "Method not implemented.");
	}
	generateGSISK(index: number, key: object | undefined): string {
		throw new BasicError("Not implemented", "Method not implemented.");
	}
	getGlobalIndices(): object {
		return {};
	}
	getColumnsForExport(): string[] {
		throw new BasicError("Not implemented", "Method not implemented.");
	}
	getObjectForExport(): object {
		throw new BasicError("Not implemented", "Method not implemented.");
	}
}
